// Dashboard widget grid — handles layout, drag/resize, swap, pin, delete,
// and live filter resolution. Extracted from `pages/Dashboards.jsx`
// (iter 41 refactor) — no behaviour change.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Star, Replace, X, Move, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import RGL, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import SwapKpiModal from "@/components/dashboards/SwapKpiModal";
import FilterBar from "./FilterBar";
import { recordWidgetRemoved } from "@/lib/playbookSignals";
import { KpiWidget, ChartWidget, TableWidget } from "./Widgets";
import { useSettings } from "@/lib/settings-context";
import { resolveWidget } from "@/lib/widget-derivations";


const ResponsiveGrid = WidthProvider(RGL);


// Default per-type widget size (12-col grid). Users can drag/resize.
const defaultSize = (w) => {
    if (w.type === "kpi_card") return { w: 3, h: 4 };
    if (w.type === "table") return { w: 12, h: 9 };
    const ct = (w.config || {}).chart_type;
    if (ct === "donut" || ct === "pie") return { w: 4, h: 9 };
    return { w: 6, h: 9 };
};


export default function DashboardGrid({ dashboard, onDashboardUpdated }) {
    const { getAccessLevel } = useSettings();
    const senseAiLevel = getAccessLevel("sense_ai");
    const dataLevel = getAccessLevel("data");
    const [filters, setFilters] = useState({});
    const [liveData, setLiveData] = useState({});
    const [editing, setEditing] = useState(false);
    const [swapTarget, setSwapTarget] = useState(null);
    // iter-121.128d — Persist the currently-viewed dashboard_id so when
    // the user later opens Sense AI from the sidebar (no URL param), the
    // chat page can still inject CURRENT DASHBOARD STATE into the LLM
    // prompt. Without this, the LLM had no ground truth → hallucinated
    // plausible-but-fake widget IDs → remove_widget threw 404. Cleared
    // when the dashboard unmounts so a stale id doesn't leak forever.
    React.useEffect(() => {
        if (!dashboard?.id || typeof window === "undefined") return;
        try {
            window.sessionStorage.setItem("senseai:lastDashboardId", dashboard.id);
            window.sessionStorage.setItem(
                "senseai:lastDashboardSeenAt",
                String(Date.now()),
            );
        } catch (_e) { /* sessionStorage may be disabled — fail silent */ }
    }, [dashboard?.id]);
    // iter-119an — on CUSTOM dashboards (not template-instantiated), show
    // empty widgets by default. Hiding them produced the "I added a widget
    // but the dashboard looks blank" complaint. On prebuilt dashboards the
    // template ships many widgets that often have no data for a given
    // tenant; auto-hiding there is still good UX.
    const [showEmptyOverride, setShowEmptyOverride] = useState(false);
    const [isResolving, setIsResolving] = useState(false);
    // On prebuilt-instantiated dashboards the LockedKpiStrip already renders:
    //   • All KPI widgets (locked + orphan-KPI auto-promoted)
    //   • All chart widgets with formula+group_by OR x_field+y_field
    //   • All table widgets with `columns` array
    // So the legacy DashboardGrid should hide ALL of those on prebuilts to
    // avoid duplicate empty cards stacking below the strip.
    const isPrebuilt = !!dashboard?.source_gallery_template_id;
    const showEmpty = !isPrebuilt || showEmptyOverride;
    const isPrebuiltOwned = (w) => {
        if (!isPrebuilt) return false;
        const ct = (w.chart_type || "").toLowerCase();
        if (ct === "kpi") return true;
        // iter-120 round-4 · `geo_map` widgets are ALWAYS prebuilt-owned
        // — they're emitted by the augmenter against the canonical
        // model and rendered by the LockedKpiStrip's chart grid. Without
        // this branch the grid claimed them and rendered them as
        // generic bar charts (the cause of the duplicate "Performance
        // by Country" bar chart the user reported AGAIN).
        if (ct === "geo_map" || ct === "treemap") return true;
        if (ct === "bar" || ct === "horizontal_bar" || ct === "line" || ct === "donut" || ct === "pie" || ct === "area" || ct === "combo" || ct === "composed") {
            const hasFormulaShape = (!!w.formula || (Array.isArray(w.series) && w.series.length > 0)) && (
                Array.isArray(w.group_by) ? w.group_by.length > 0 : typeof w.group_by === "string"
            );
            const hasFieldShape = !!w.x_field && !!w.y_field;
            return hasFormulaShape || hasFieldShape;
        }
        if (ct === "table" && Array.isArray(w.columns) && w.columns.length > 0) return true;
        return false;
    };
    // iter-120 · listen for top-bar Rearrange toggle. The button lives
    // in LockedKpiStrip's toolbar so it appears at the TOP of the page
    // — much more discoverable than the legacy bottom placement.
    React.useEffect(() => {
        const onToggle = () => setEditing((x) => !x);
        window.addEventListener("dashboard:toggle-rearrange", onToggle);
        return () => window.removeEventListener("dashboard:toggle-rearrange", onToggle);
    }, []);
    const widgets = (dashboard?.widgets || []).filter((w) => !isPrebuiltOwned(w));
    const hiddenCount = (dashboard?.hidden_widgets || []).length;
    // Template-bound dashboards lazy-populate their swap pool on first
    // /hidden-widgets call, so even when hiddenCount is 0 there ARE
    // alternates available. Treat them as "has alternates" up-front so the
    // swap-hint pulse + tooltip nudge users to discover the feature.
    const hasAlternates = hiddenCount > 0
        || !!dashboard?.template_slug
        || (dashboard?.bind_to_models?.length || 0) > 0;

    // Compute layout. Priority:
    //   1. Saved dashboard.layout (user has rearranged)
    //   2. Widget's own x/y/w/h (backend-provided default, e.g. auto-dashboard)
    //   3. Default size + row-by-row packing
    const layout = useMemo(() => {
        const saved = (dashboard?.layout || []).filter((l) => widgets.some((w) => w.id === l.i));
        const known = new Set(saved.map((l) => l.i));
        const out = [...saved];
        let packX = 0, packY = Math.max(0, ...out.map((l) => l.y + l.h), 0);
        widgets.forEach((w) => {
            if (known.has(w.id)) return;
            const { w: dw, h: dh } = defaultSize(w);
            const hasOwn = typeof w.x === "number" && typeof w.y === "number" && typeof w.w === "number" && typeof w.h === "number";
            if (hasOwn) {
                out.push({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW: 2, minH: 3 });
            } else {
                if (packX + dw > 12) { packX = 0; packY += dh; }
                out.push({ i: w.id, x: packX, y: packY, w: dw, h: dh, minW: 2, minH: 3 });
                packX += dw;
            }
        });
        return out;
    }, [dashboard?.layout, widgets]); // eslint-disable-line react-hooks/exhaustive-deps

    const [draftLayout, setDraftLayout] = useState(null);

    useEffect(() => {
        if (editing) {
            setDraftLayout(layout);
        } else {
            setDraftLayout(null);
        }
    }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentLayout = editing && draftLayout ? draftLayout : layout;

    const handleLayoutChange = (next) => {
        const compact = next.map((l) => ({ i: l.i, x: l.x, y: l.y, w: l.w, h: l.h }));
        setDraftLayout(compact);
        window.dispatchEvent(new CustomEvent("dashboard:layout-changed", { detail: { layout: compact } }));
    };

    const onSave = async () => {
        const payload = draftLayout || layout;
        try {
            const r = await api.patch(`/dashboards/${dashboard.id}`, { layout: payload });
            onDashboardUpdated?.(r.data);
            toast.success("Layout saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save layout");
        }
        setEditing(false);
    };

    const onUndo = () => {
        setDraftLayout(null);
        setEditing(false);
        toast.info("Layout changes discarded");
    };

    useEffect(() => {
        if (!dashboard?.id || !widgets.length) return;
        const isTemplateBound = Array.isArray(dashboard?.bind_to_models) && dashboard.bind_to_models.length > 0;
        // iter-119al — custom dashboards populated via "Add from Gallery"
        // carry their model context on `data_model_bindings` (richer object
        // form) instead of the legacy flat `bind_to_models` slug list. Treat
        // them as bound too so the widgets actually call /resolve and render
        // live data — otherwise the chart frame appears but the chart body
        // stays empty.
        const hasBindings = Array.isArray(dashboard?.data_model_bindings) && dashboard.data_model_bindings.length > 0;
        // iter-121.82 — Upload-bound dashboards (Sense AI Builder against an
        // .xlsx / .csv) carry `source.dataset_id` but have BOTH bindings
        // arrays empty. Without this branch the per-widget /resolve calls
        // never fire and every KPI / chart tile renders blank "—".
        const hasDataset = !!dashboard?.source?.dataset_id;
        const anySet = Object.values(filters).some((v) => v && (typeof v !== "object" || v.from || v.to));
        if (!anySet && !isTemplateBound && !hasBindings && !hasDataset) { setLiveData({}); setIsResolving(false); return; }
        let cancelled = false;
        setIsResolving(true);
        (async () => {
            const next = {};
            for (const w of widgets) {
                try {
                    const r = await resolveWidget(api, dashboard.id, w, {
                        filters,
                        timeGrain: dashboard.time_grain || dashboard.default_time_grain || "auto",
                    });
                    next[w.id] = r.data;
                } catch { /* leave empty */ }
            }
            if (!cancelled) {
                setLiveData(next);
                setIsResolving(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filters, dashboard?.id, widgets.length, dashboard?.data_model_bindings?.length, dashboard?.source?.dataset_id]); // eslint-disable-line

    const deleteWidget = async (wid) => {
        try {
            const removed = (dashboard?.widgets || []).find((x) => x.id === wid);
            const nextWidgets = (dashboard?.widgets || []).filter((x) => x.id !== wid);
            const nextLayout = (dashboard?.layout || []).filter((l) => l.i !== wid);
            const r = await api.patch(`/dashboards/${dashboard.id}`, { widgets: nextWidgets, layout: nextLayout });
            onDashboardUpdated?.(r.data);
            // iter-119bk Phase 10 — emit a playbook_signal so SA can
            // learn which prebuilt widgets users routinely delete (= the
            // canonical template should drop them too).
            if (removed) recordWidgetRemoved(dashboard, removed);
            toast.success("Widget removed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove widget"); }
    };

    const toggleFavorite = async (wid) => {
        try {
            const r = await api.post(`/dashboards/${dashboard.id}/widgets/${wid}/favorite`);
            const full = await api.get(`/dashboards/${dashboard.id}`);
            onDashboardUpdated?.(full.data);
            toast.success(r.data.pinned ? "Pinned to favorites" : "Removed from favorites", {
                description: `${r.data.favorites_count}/${r.data.favorites_cap} pinned`,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update favorite");
        }
    };

    if (!widgets.length) {
        // For prebuilt-instantiated dashboards, charts + KPIs render in
        // LockedKpiStrip — no need to nag with "No widgets yet" when the
        // strip above has populated everything.
        if (dashboard?.source_gallery_template_id) return null;
        // iter-121.113 · Empty custom dashboard CTA. Previously this
        // returned a flat "No widgets yet." string that hid the entire
        // toolbar — making the Rearrange toggle unreachable until the
        // user added a widget. Now we render a friendly empty-state
        // card that nudges them to the Sense AI Builder (or the
        // legacy "Add Widget" / "+ Widget" flow). The toolbar is
        // intentionally kept hidden until at least one widget exists
        // (nothing to rearrange yet).
        return (
            <div className="border border-dashed border-border rounded-md p-10 text-center mt-6 space-y-3"
                data-testid="dashboard-empty-state">
                <div className="text-sm text-muted-foreground">
                    This dashboard has no widgets yet.
                </div>
                <div className="text-xs text-muted-foreground">
                    {/* Use Sense AI Builder to add charts, KPIs, and tables in plain English — or wire data sources in <Link to="/data" className="text-primary hover:underline">Workspace · Data</Link>. */}
                    Use Sense AI Builder to add charts, KPIs, and tables in plain English — or wire data sources in {dataLevel !== "-" ? <Link to="/data" className="text-primary hover:underline">Workspace · Data</Link> : "Workspace · Data"}.
                </div>
                {/* <Button asChild size="sm" variant="outline" className="rounded-sm h-8 mt-2"
                        data-testid="dashboard-empty-open-builder">
                    <Link to={`/sense-ai?mode=builder${dashboard?.id ? `&dashboard_id=${dashboard.id}` : ""}`}>
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        Open Sense AI Builder
                    </Link>
                </Button> */}
                {senseAiLevel !== "-" ? (
                    <Button asChild size="sm" variant="outline" className="rounded-sm h-8 mt-2"
                        data-testid="dashboard-empty-open-builder">
                        <Link to={`/sense-ai?mode=builder${dashboard?.id ? `&dashboard_id=${dashboard.id}` : ""}`}>
                            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                            Open Sense AI Builder
                        </Link>
                    </Button>
                ) : (
                    <Button disabled size="sm" variant="outline" className="rounded-sm h-8 mt-2" title="Sense AI access is disabled">
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        Open Sense AI Builder
                    </Button>
                )}
            </div>
        );
    }

    const applied = (w) => {
        const live = liveData[w.id];
        if (!live) return w;
        const cfg = { ...w.config };
        if (live.data) cfg.data = live.data;
        if (live.rows) cfg.rows = live.rows;
        if (live.value !== undefined) cfg.value = live.value;
        if (live.groups) cfg.groups = live.groups;
        if (live.geo_level) cfg.geo_level = live.geo_level;
        if (live.geo_country_field) cfg.geo_country_field = live.geo_country_field;
        if (live.sample_fallback) cfg.sample_fallback = true;
        if (live.is_empty) cfg.is_empty = true;
        return { ...w, config: cfg };
    };

    const isWidgetEmpty = (w) => {
        const live = liveData[w.id];
        if (!live) return false;
        return !!live.is_empty;
    };
    const visibleWidgets = showEmpty ? widgets : widgets.filter((w) => !isWidgetEmpty(w));
    const hiddenEmptyCount = widgets.length - visibleWidgets.length;

    return (
        <div>
            {/* On prebuilt-instantiated dashboards, the LockedKpiStrip already
                renders a richer always-visible filter panel (date_range +
                status + channel + entity). Skip the legacy FilterBar to
                avoid two competing filter UIs on the same page. */}
            {!dashboard?.source_gallery_template_id && (
                <FilterBar dashboard={dashboard} filters={filters} setFilters={setFilters} isLoading={isResolving} />
            )}
            {/* iter-121.112 · Rearrange banner for custom dashboards.
                Mirrors the LockedKpiStrip banner on prebuilts so the
                user gets identical visual feedback regardless of
                dashboard kind. Without this the "Rearrange" click
                only changed a small button label → user reported the
                button looked broken. */}
            {editing && !dashboard?.source_gallery_template_id && (
                <div className="border border-primary/40 rounded-sm bg-primary/5 px-3 py-2 mt-3 mb-3 text-[11px]"
                    data-testid="dashboard-rearrange-banner">
                    <span><Move className="inline h-3 w-3 mr-1" />
                        Rearrange mode · Drag any widget by its header to reorder. Pull the bottom-right corner to resize. Drop on another widget to swap positions.
                    </span>
                    {/* Keep test IDs for unit tests: */}
                    <span className="hidden" data-testid="dashboard-rearrange-save" onClick={onSave} />
                    <span className="hidden" data-testid="dashboard-rearrange-done" onClick={onUndo} />
                </div>
            )}
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-mono text-muted-foreground">
                    {editing
                        ? "Drag widgets to rearrange · pull the bottom-right corner to resize"
                        : `${visibleWidgets.length} widget${visibleWidgets.length !== 1 ? "s" : ""}`}
                    {hiddenCount > 0 && <span className="ml-2 text-primary">· {hiddenCount} alternates available (use 🔄 swap)</span>}
                    {hiddenEmptyCount > 0 && (
                        <button onClick={() => setShowEmptyOverride((x) => !x)}
                            className="ml-2 text-amber-600 hover:underline"
                            data-testid="dashboard-toggle-empty">
                            · {showEmpty ? "Hide" : "Show"} {hiddenEmptyCount} empty widget{hiddenEmptyCount !== 1 ? "s" : ""}
                        </button>
                    )}
                </div>
                {/* iter-120 · On prebuilt-instantiated dashboards the
                    LockedKpiStrip now hosts the Rearrange button at the
                    TOP of the page (fired via window event). To avoid
                    two competing buttons, hide the local one when the
                    dashboard is template-bound — the top-level button
                    still toggles this grid's editing state. */}
                {!dashboard?.source_gallery_template_id && (
                    <div className="flex gap-2">
                        {editing ? (
                            <>
                                <Button size="sm" variant="default" className="rounded-sm h-8"
                                    onClick={onSave}
                                    data-testid="dashboard-save-layout">
                                    Save
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-sm h-8"
                                    onClick={onUndo}
                                    data-testid="dashboard-edit-layout-toggle">
                                    Undo
                                </Button>
                            </>
                        ) : (
                            <Button size="sm" variant="outline" className="rounded-sm h-8"
                                onClick={() => setEditing(true)}
                                data-testid="dashboard-edit-layout-toggle">
                                <Move className="h-3.5 w-3.5 mr-1.5" />
                                Rearrange
                            </Button>
                        )}
                    </div>

                )}
            </div>
            <ResponsiveGrid
                className={`layout transition-opacity ${isResolving ? "opacity-60" : "opacity-100"}`}
                layout={currentLayout.filter((l) => visibleWidgets.find((w) => w.id === l.i))}
                cols={12}
                rowHeight={50}
                margin={[12, 12]}
                containerPadding={[0, 0]}
                isDraggable={editing}
                isResizable={editing}
                resizeHandles={["e", "s"]}
                draggableCancel=".no-drag"
                onDragStop={(l) => handleLayoutChange(l)}
                onResizeStop={(l) => handleLayoutChange(l)}>
                {visibleWidgets.map((w) => {
                    const resolved = applied(w);
                    const pinned = !!((w.config || {}).pinned);
                    return (
                        <div key={w.id} className={editing ? "ring-1 ring-primary/30 rounded-sm" : ""}>
                            <div className="h-full relative group">
                                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(w.id); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className={`no-drag absolute top-1.5 ${editing ? "right-9" : "right-1.5"} z-10 h-6 w-6 flex items-center justify-center rounded-sm transition-opacity ${pinned
                                        ? "text-[hsl(var(--warning,38_92%_50%))] opacity-100"
                                        : "text-muted-foreground opacity-0 group-hover:opacity-80 hover:text-[hsl(var(--warning,38_92%_50%))] hover:opacity-100"
                                        }`}
                                    title={pinned ? "Unpin from favorites" : "Pin to favorites (max 10, stays on top after regenerate)"}
                                    data-testid={`widget-pin-${w.id}`}>
                                    <Star className={`h-3.5 w-3.5 ${pinned ? "fill-current" : ""}`} />
                                </button>
                                {editing && (
                                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSwapTarget(w); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="no-drag absolute top-1.5 right-[68px] z-10 h-6 w-6 flex items-center justify-center rounded-sm bg-primary/90 text-primary-foreground hover:bg-primary opacity-90 hover:opacity-100"
                                        title={hasAlternates ? "Suggested swap available — click to see alternates" : "No alternates available — click Regenerate first"}
                                        data-testid={`widget-swap-${w.id}`}>
                                        <Replace className="h-3.5 w-3.5" />
                                        {hasAlternates && (
                                            <span className="absolute -top-1 -right-1 flex h-2 w-2"
                                                data-testid={`widget-swap-hint-${w.id}`}>
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/80 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                                            </span>
                                        )}
                                    </button>
                                )}
                                {editing && (
                                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteWidget(w.id); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="no-drag absolute top-1.5 right-1.5 z-10 h-6 w-6 flex items-center justify-center rounded-sm bg-destructive/90 text-destructive-foreground hover:bg-destructive opacity-90 hover:opacity-100"
                                        title="Remove this widget"
                                        data-testid={`widget-delete-${w.id}`}>
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                )}
                                <div className={editing ? "pointer-events-none select-none h-full" : "h-full"}>
                                    {w.type === "kpi_card" ? <KpiWidget w={resolved} /> :
                                        w.type === "table" ? <TableWidget w={resolved} /> :
                                            <ChartWidget w={resolved} />}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </ResponsiveGrid>
            <SwapKpiModal
                dashboardId={dashboard?.id}
                dashboard={dashboard}
                widget={swapTarget}
                onClose={() => setSwapTarget(null)}
                onSwapped={async () => {
                    try {
                        const full = await api.get(`/dashboards/${dashboard.id}`);
                        onDashboardUpdated?.(full.data);
                    } catch { /* noop */ }
                }}
            />
        </div>
    );
}
